import { decodeFromBase64 } from '~/utils/message';
import { isCitationBulletVisibleOnDoc } from '~/utils/citations-utils';

export const footnotesWithAuthorsTitleYearPage = (footnote, displayIndex = true) => {
  const { citation_metadata, pageNumber = [] } = footnote;

  if (citation_metadata && Object.values(citation_metadata).length) {
    let authors = '';

    if (citation_metadata?.authors?.length) {
      const formattedAuthors = citation_metadata.authors.map((author) => {
        const initials = author.first_name
          .split(' ')
          .map((n) => n[0]?.toUpperCase() + '.')
          .join('');
        return author.last_name && initials ? `${author.last_name} ${initials}` : '';
      });

      authors =
        formattedAuthors.length &&
        (formattedAuthors.length > 6
          ? formattedAuthors.slice(0, 6).join(', ') + ', et al.'
          : formattedAuthors.join(', '));
    }

    const title = citation_metadata?.title ? citation_metadata?.title.trim() : '';
    const year = citation_metadata?.year ? citation_metadata?.year.trim() : '';
    const pages = footnote?.page || pageNumber.join(', ');

    const referenceParts = [
      authors?.length ? authors : null,
      title?.trim() || null, // Ensure title is trimmed and valid
      year?.trim() || null,
      pages ? `(${pages})` : null, // Ensure pages are wrapped in parentheses
    ];

    const reference = referenceParts
      .filter(Boolean) // Remove falsy values
      .join(', ') // Join with commas
      .replace(/, \(/, ' (') // Remove the comma before pages
      .replace(/, $/, ''); // Remove trailing comma if it exists

    return displayIndex ? `${footnote.dataLabel}. ${reference}` : reference;
  }

  return `${footnote.dataLabel}. ${footnote.customFootnote || ''}`;
};

export const customFootnoteOrDefault = (footnotes = [], displayIndex = true) =>
  footnotes.map((footnote) => {
    const { customFootnote } = footnote;

    if (customFootnote) {
      return displayIndex ? `${footnote.dataLabel}. ${customFootnote}` : customFootnote;
    }

    return footnotesWithAuthorsTitleYearPage(footnote, displayIndex);
  });

export const getSnippetData = (footnote) => {
  if (!footnote) {
    return '';
  }

  const { snippets = {}, page, snippetIndex } = footnote;

  const key = `${Number(page)}-${Number(snippetIndex)}`;
  const snippetText = decodeFromBase64(snippets[key] || '');

  return snippetText;
};

export const footnoteParsedData = (footnote) => {
  if (!footnote) {
    return { page: '', snippet: '' };
  }
  const { pageNumber = [], snippets = {} } = footnote;

  const pages = pageNumber ? pageNumber?.join(', ') : '';
  const firstSnippetKey = Object.keys(snippets)[0];
  const index = firstSnippetKey?.split('-');
  const snippetText = getSnippetData({ snippets, page: pageNumber[0], snippetIndex: index?.[1] || 1 });

  return { page: pages, snippet: snippetText };
};

export const mapReferencesFromBullets = (htmlString, footnotes) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  const refs = [];
  const bullets = doc.querySelectorAll('span[data-doc-id]');
  const visibleBullets = Array.from(bullets).filter(isCitationBulletVisibleOnDoc);

  if (visibleBullets && visibleBullets.length) {
    visibleBullets.forEach((bullet) => {
      Array.from(bullet.attributes).reduce((acc, attr) => {
        acc[attr.name] = attr.value;
        if (attr.name === 'data-doc-id') {
          const currentRef = footnotes?.find((ref) => (ref.externalId || ref.id) === attr.value);
          const dataLabel = Number(bullet.attributes['data-label']?.value);
          if (currentRef) {
            const refWithDataLabelAndPage = {
              ...currentRef,
              page: bullet.attributes['data-page']?.value,
              dataLabel,
            };
            refs.push(refWithDataLabelAndPage);
          }
        }
        return acc;
      }, {});
    });

    refs.sort((a, b) => parseFloat(a.dataLabel) - parseFloat(b.dataLabel));
  } else {
    console.log('No bullets found.');
  }

  return [...new Set(refs.map((obj) => JSON.stringify(obj)))].map((str) => JSON.parse(str));
};

// Locate and remove footnotes elements in previous DOM structure
export const removePreviousFootnotesFromLayout = ({ layout, sendMessageToDocument }) => {
  const lastChild = layout.children?.length ? layout.children[layout?.children?.length - 1] : null;
  if (lastChild?.element?.text?.includes('References')) {
    let sectionFound = false;

    const pathSplit = lastChild.cssPath
      .split('>')
      .reverse()
      .filter((pathItem) => {
        if (pathItem.match(/^mj-section/)) {
          sectionFound = true;
        }
        return sectionFound;
      })
      .reverse();

    if (pathSplit.length) {
      sendMessageToDocument({
        method: 'removeElement',
        params: {
          query: pathSplit.join('>'),
        },
      });
    }
  }
};

export const removeFootnotesFromLayout = ({ layout, sendMessageToDocument }) => {
  // Calls remove when the "show footnotes" value on each layout is changed to true....
  const footnotesElement = layout.children?.find((child) => child.attributes['data-ant-field'] === 'reference-list');
  if (footnotesElement) {
    sendMessageToDocument({
      method: 'removeElement',
      params: {
        query: `${layout.cssPath} [data-ant-field="reference-parent"]`,
      },
    });
  } else {
    removePreviousFootnotesFromLayout({ layout, sendMessageToDocument });
  }
};

const getFontStyleFromTextsEntireLayout = async ({ layout, sendAsyncMessageToDocument }) => {
  // Query all mj-sections under the layout
  const textQuery = `${layout.cssPath} > mj-section mj-text`;
  const {
    data: { response: elements },
  } = await sendAsyncMessageToDocument({
    method: 'getSelectableElementsData',
    params: { query: textQuery },
  });
  if (!elements?.length) return null;

  // Walk backwards (last to first) until we find a styled text
  for (let i = elements.length - 1; i >= 0; i--) {
    const el = elements[i];
    if (el?.attributes?.color || el?.attributes?.['font-family'] || el?.attributes?.['font-size']) {
      return el;
    }
  }

  return null;
};

const renderFootnotesInPredefinedLayout = async ({ layout, footnotes, sendMessageToDocument }) => {
  if (!layout) return;

  const footnotesRefs = customFootnoteOrDefault(footnotes);

  const contentToUpdate = `<p>
      ${footnotesRefs.join('<br />')}
     </p>`;

  const footnotesElement = layout.children?.find((child) => child.attributes['data-ant-field'] === 'reference-list');

  sendMessageToDocument({
    method: 'setContent',
    params: {
      query: footnotesElement?.cssPath,
      content: contentToUpdate,
      select: false,
    },
  });
};

export const renderFootnotesInLayout = async ({
  t,
  layout,
  footnotes,
  sendMessageToDocument,
  sendAsyncMessageToDocument,
}) => {
  if (!layout) return;

  const footnotesElement = layout.children?.find((child) => child.attributes['data-ant-field'] === 'reference-list');

  // Remove references in bottom if there are no reference bullets in the document
  if (!footnotes?.length) {
    removeFootnotesFromLayout({ layout, sendMessageToDocument });
    return;
  }

  const footnotesRefs = customFootnoteOrDefault(footnotes);

  if (!footnotesElement) {
    removePreviousFootnotesFromLayout({ layout, sendMessageToDocument });

    const lastSectionQuery = `${layout.cssPath} > mj-section:last-child`;
    const lastColumnQuery = `${layout.cssPath} > mj-section:last-child mj-column`;
    const lastTextQuery = `${layout.cssPath} > mj-section:last-child mj-text`;

    // Getting all styling data for each element
    const [
      {
        data: { response: lastSection },
      },
      {
        data: { response: lastColumn },
      },
      {
        data: { response: lastTexts },
      },
    ] = await Promise.all([
      sendAsyncMessageToDocument({
        method: 'getElementData',
        params: { query: lastSectionQuery },
      }),
      sendAsyncMessageToDocument({ method: 'getElementData', params: { query: lastColumnQuery } }),
      sendAsyncMessageToDocument({ method: 'getSelectableElementsData', params: { query: lastTextQuery } }),
    ]);
    let lastText = lastTexts?.[lastTexts?.length - 1] || null;

    if (!lastText) {
      lastText = await getFontStyleFromTextsEntireLayout({ layout, sendAsyncMessageToDocument });
    }

    const backgroundColor =
      lastColumn?.attributes['background-color'] &&
      lastColumn?.attributes['background-color'] !== 'rgba(255, 255, 255, 0)'
        ? lastColumn?.attributes['background-color']
        : lastSection?.attributes['background-color'];

    const copyStyles = ['color', 'font-family', 'font-size'];
    const textStyles = lastText
      ? copyStyles.reduce((acc, style) => {
          const value = lastText.attributes?.[style] || lastText.styles?.[style];
          if (!value) return acc;
          if (style === 'font-size') {
            return `${acc} ${style}="12px"`; // enforce 12px
          }
          return `${acc} ${style}="${value}"`;
        }, '')
      : '';

    const elementToInsert = `
      <mj-section data-ant-field="reference-parent" background-color="${backgroundColor}">
        <mj-column>
          <mj-text data-ant-field="reference-list" ${textStyles}>
            <p>
              <b>${t('author.references')}</b><br /><br />
              ${footnotesRefs.join('<br />')}
            </p>
          </mj-text>
        </mj-column>
      </mj-section>`;

    sendMessageToDocument({
      method: 'addElement',
      params: {
        parent: layout.cssPath,
        element: elementToInsert,
        select: false,
      },
    });
  } else {
    const contentToUpdate = `<p>
      <b>${t('author.references')}</b><br /><br />
      ${footnotesRefs.join('<br />')}
     </p>`;

    sendMessageToDocument({
      method: 'setContent',
      params: {
        query: footnotesElement?.cssPath,
        content: contentToUpdate,
        select: false,
      },
    });
  }
};

export const updateDocumentLayoutsShowFootnotesToFalse = ({
  currentDesignSystemDocumentElements,
  sendMessageToDocument,
}) => {
  currentDesignSystemDocumentElements.forEach((element) => {
    if (element.attributes?.['show-footnotes'] === 'true') {
      sendMessageToDocument({
        method: 'updateAttribute',
        params: {
          query: element.cssPath,
          attr: 'show-footnotes',
          value: 'false',
        },
      });

      removeFootnotesFromLayout({ layout: element, sendMessageToDocument });
      element.attributes['show-footnotes'] = 'false';
    }
  });
};

const handleFieldVisibility = async (isVisible, cssPath, sendAsyncMessageToDocument, sendMessageToDocument) => {
  const elementAttributes = await sendAsyncMessageToDocument({
    method: 'getAttributes',
    params: {
      query: cssPath,
    },
  });

  const hidden = elementAttributes?.data?.response?.hidden;
  if (hidden) {
    sendMessageToDocument({
      method: 'updateAttribute',
      params: {
        query: cssPath,
        attr: 'hidden',
        value: isVisible,
      },
    });
  }
};

export const displayAllFreshReferencesAtTheEnd = async ({
  htmlString,
  currentDesignSystemDocumentElements,
  footnotes,
  sendMessageToDocument,
  sendAsyncMessageToDocument,
  t,
}) => {
  const allReferences = mapReferencesFromBullets(htmlString, footnotes);

  const elementsWithReferences = Object.values(currentDesignSystemDocumentElements).filter((obj) =>
    obj?.children?.some((child) => child.attributes?.['data-ant-field'] === 'reference-list'),
  );

  if (elementsWithReferences.length > 1) {
    removeReferencesFromLastLayout({ currentDesignSystemDocumentElements, sendMessageToDocument });
  }

  const footnotesPredefinedElement = Object.values(currentDesignSystemDocumentElements).find((obj) =>
    obj?.children?.some((child) => child.attributes?.['data-ant-field'] === 'reference-list'),
  );

  if (footnotesPredefinedElement) {
    const childWithReferences = footnotesPredefinedElement.children.find(
      (child) => child.attributes['data-ant-field'] === 'reference-list',
    );

    await handleFieldVisibility(
      childWithReferences.attributes.hidden,
      childWithReferences.cssPath,
      sendAsyncMessageToDocument,
      sendMessageToDocument,
    );

    await renderFootnotesInPredefinedLayout({
      layout: footnotesPredefinedElement,
      footnotes: allReferences,
      sendMessageToDocument,
    });
  } else {
    const lastLayout =
      currentDesignSystemDocumentElements?.length &&
      currentDesignSystemDocumentElements[currentDesignSystemDocumentElements?.length - 1];

    await renderFootnotesInLayout({
      t,
      layout: lastLayout,
      footnotes: allReferences,
      sendMessageToDocument,
      sendAsyncMessageToDocument,
    });
  }
};

const removeReferencesFromLastLayout = ({ currentDesignSystemDocumentElements, sendMessageToDocument }) => {
  const lastLayout = currentDesignSystemDocumentElements[currentDesignSystemDocumentElements?.length - 1];
  removeFootnotesFromLayout({ layout: lastLayout, sendMessageToDocument });
};

export const removeOrHideDocumentReferences = ({ currentDesignSystemDocumentElements, sendMessageToDocument }) => {
  const footnotesPredefinedElement = Object.values(currentDesignSystemDocumentElements).find((obj) =>
    obj?.children?.some((child) => child.attributes?.['data-ant-field'] === 'reference-list'),
  );

  // if we have a predefined field for references, we have only to hide it;
  // if References were added at the end of the document, they will be deleted
  if (footnotesPredefinedElement) {
    const elementToBeHidden = footnotesPredefinedElement.children.find(
      (child) => child.attributes['data-ant-field'] === 'reference-list',
    );

    sendMessageToDocument({
      method: 'updateAttribute',
      params: {
        query: elementToBeHidden.cssPath,
        attr: 'hidden',
        value: true,
      },
    });
  } else {
    removeReferencesFromLastLayout({ currentDesignSystemDocumentElements, sendMessageToDocument });
  }
};

export const refreshReferencesData = async ({
  displayReferencesOnDocumentLevel,
  htmlString,
  currentDesignSystemDocumentElements,
  footnotes,
  sendMessageToDocument,
  sendAsyncMessageToDocument,
  t,
  updateEditorSelectableElementsData,
  saveEditorContentStateFromIframe,
}) => {
  if (displayReferencesOnDocumentLevel) {
    await displayAllFreshReferencesAtTheEnd({
      htmlString,
      currentDesignSystemDocumentElements,
      footnotes,
      sendMessageToDocument,
      sendAsyncMessageToDocument,
      t,
    });
  } else {
    await refreshReferencesForEachLayout({
      currentDesignSystemDocumentElements,
      footnotes,
      t,
      sendMessageToDocument,
      sendAsyncMessageToDocument,
    });
  }
  updateEditorSelectableElementsData();
  saveEditorContentStateFromIframe();
};

const refreshReferencesForEachLayout = async ({
  currentDesignSystemDocumentElements,
  footnotes,
  t,
  sendMessageToDocument,
  sendAsyncMessageToDocument,
}) => {
  await Promise.all(
    currentDesignSystemDocumentElements?.map((layout) => {
      if (layout?.attributes?.['show-footnotes'] === 'true') {
        const htmlString = layout?.element?.html;
        const referencesCurrentLayout = mapReferencesFromBullets(htmlString, footnotes, true);

        return renderFootnotesInLayout({
          t,
          layout,
          footnotes: referencesCurrentLayout,
          sendMessageToDocument,
          sendAsyncMessageToDocument,
        });
      }
      return true;
    }),
  );
};

export const updateReferencesListForPreviewMode = (referencesContainer, dict, footnotes) => {
  if (!referencesContainer || !footnotes) return;

  // Get lines of references
  const lines = Array.from(referencesContainer.childNodes)
    .map((n) => n.textContent.trim())
    .filter(Boolean);

  // Clean text: remove numbering, page numbers, punctuation
  const cleaned = lines.map((line) => {
    let text = line
      .replace(/^\d+\.\s*/, '') // remove leading numbering
      .replace(/^[,;:\s]+/, '') // remove leading punctuation & spaces
      .replace(/;\s*\(\d+\)/g, '') // remove "; (n)"
      .replace(/\(\d+\)/g, '') // remove "(n)"
      .trim();

    text = text.replace(/[,;:\s]+$/, ''); // remove trailing punctuation
    return text;
  });

  const seen = new Set();
  let isPredefinedFieldForFootnotes = true;
  const uniqueRefs = cleaned.filter((r) => {
    if (r.toLowerCase() === 'references') {
      // if first elem of cleaned is "References", then we don't have a predefined field for footnotes
      isPredefinedFieldForFootnotes = false;
      return false;
    }
    if (!seen.has(r)) {
      seen.add(r);
      return true;
    }
    return false;
  });

  let references = uniqueRefs
    .map((ref) => {
      // Find the footnote whose title appears in this reference
      const matchedFootnote = footnotes.find(
        (fn) =>
          (fn.customFootnote && ref.includes(fn.customFootnote)) ||
          (fn.citation_metadata?.title && ref.includes(fn.citation_metadata.title)),
      );
      let index = '?';
      if (matchedFootnote) {
        const docId = matchedFootnote.externalId || matchedFootnote.id;
        if (docId && dict[docId]) {
          index = dict[docId];
        }
      }

      return `${index}. ${ref}`;
    })
    .join('<br />');

  // Build HTML using dict and footnotes to assign indexes
  if (!isPredefinedFieldForFootnotes) {
    referencesContainer.innerHTML = `<b>References</b><br /><br />` + references;
  } else {
    referencesContainer.innerHTML = references;
  }
};
